import "./list-contract.scss";

import { GetContractModel } from "@factory/model/query-model-contract";
import { useQyGetContract } from "@factory/query/contract.query";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { FilterContract } from "../filter-contract/filter-contract";
import { useFilterContractContext } from "../filter-contract/filter-contract.context";
import { getPaymentSeverity } from "@core/utility/get-severity";

const STATUS_COUNT_FIELDS: Array<{ field: string; label: string }> = [
  { field: "unpaid_billing_count", label: "Unpaid" },
  { field: "partial_billing_count", label: "Partial" },
  { field: "paid_billing_count", label: "Paid" },
  { field: "waived_billing_count", label: "Waived" },
  { field: "overpaid_billing_count", label: "Overpaid" },
];

export function ListContract() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterContractContext();
  const { formatDateLocal } = useDateTimeContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: contractResponse,
    isLoading,
    isError,
    error,
  } = useQyGetContract(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues(),
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetContractModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const renderPaymentSummary = (contract: any) => {
    const totalPaid = contract.total_amount_paid || 0;
    const balance = contract.total_balance || 0;
    const creditBalance = contract.credit_balance || 0;

    // The five real canonical statuses, straight from view_contract's
    // precise per-status counts - no more UNPAID+PARTIAL conflation. Only
    // non-zero counts are shown, in the same fixed order used everywhere
    // else in the app (UNPAID | PARTIAL | PAID | WAIVED | OVERPAID).
    const statusTags = STATUS_COUNT_FIELDS.map(({ field, label }) => ({
      label,
      count: parseInt(contract[field]) || 0,
      severity: getPaymentSeverity(label.toUpperCase()),
    })).filter((s) => s.count > 0);

    return (
      <div>
        <div className="mb-2">
          <div className="flex text-sm text-600 mb-1">
            <span style={{ minWidth: '65px' }}>Paid:</span>
            <span className="text-green-600 font-semibold">
              {currencyFormat(totalPaid, "PHP")}
            </span>
          </div>
          <div className="flex text-sm text-600">
            <span style={{ minWidth: '65px' }}>Balance:</span>
            <span
              className={
                balance > 0
                  ? "text-red-600 font-semibold"
                  : "text-green-600 font-semibold"
              }
            >
              {balance > 0
                ? currencyFormat(balance, "PHP")
                : creditBalance > 0
                  ? `Credit ${currencyFormat(creditBalance, "PHP")}`
                  : currencyFormat(0, "PHP")}
            </span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {statusTags.map((s) => (
            <Tag
              key={s.label}
              value={`${s.count} ${s.label}`}
              severity={s.severity}
            />
          ))}
        </div>
      </div>
    );
  };

  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        {/* <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        /> */}
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterContract />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{contractResponse?.count}</b>
      </h5>

      <DataTable
        value={contractResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column
          field="patient"
          body={(cell) => (
            <b>
              {cell.first_name} {cell.last_name}
            </b>
          )}
          header="Patient"
          sortable
        ></Column>
        <Column
          field="service"
          body={(cell) => <b>{cell.service_name}</b>}
          header="Service"
          sortable
        ></Column>
        <Column
          field="person"
          body={(cell) => (
            <b>
              {cell.person_first_name} {cell.person_last_name}
            </b>
          )}
          header="Doctor"
          sortable
        ></Column>
        <Column
          field="amount"
          body={(cell) => <b>{currencyFormat(cell.amount, "PHP")}</b>}
          header="Amount"
          sortable
        ></Column>
        <Column
          body={(cell) => renderPaymentSummary(cell)}
          header="Payment Status"
          style={{ minWidth: "250px" }}
        ></Column>
        <Column
          field="createdAt"
          body={(cell) => <b>{formatDateLocal(cell.created_at)}</b>}
          header="Created At"
          sortable
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={contractResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListContract" className="contract">
      <HeaderContent title={<h1>Contract</h1>}>
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListContract;
